import { clamp, lerp, smoothstep } from './math'
import { layoutViewport } from './viewport'

export type SectionId = 'home' | 'services' | 'work' | 'process' | 'about' | 'contact'

/**
 * A camera "shot" framed on the tower for one section.
 * az/el are orbit angles around the tower axis, `fy` is the height the camera looks at,
 * pan* shift the lens (architectural shift — verticals stay straight) in fractions of the viewport.
 */
export interface Shot {
  az: number
  el: number
  dist: number
  fy: number
  panX: number
  panY: number
  twist: number
  veil: number
  signal: number
  highlight: number
}

type ShotKey = keyof Shot

const KEYS: ShotKey[] = ['az', 'el', 'dist', 'fy', 'panX', 'panY', 'twist', 'veil', 'signal', 'highlight']

// Desktop framing. Content sits left in most sections, so the tower is shifted right.
const SHOTS: Record<SectionId, Shot> = {
  // Monumental, eye-level, lens-shifted right of the headline.
  home: { az: 0.62, el: 0.04, dist: 20.5, fy: 3.85, panX: 0.25, panY: 0.02, twist: 0.012, veil: 0, signal: 0.35, highlight: 0 },
  // Rising above the plinth: the survey grid becomes the backdrop for the cards.
  services: { az: 1.45, el: 0.62, dist: 17, fy: 2.6, panX: 0.2, panY: 0, twist: 0.012, veil: 0.22, signal: 0.2, highlight: 0 },
  // Near top-down plan view, mostly veiled so the work itself leads.
  work: { az: 2.3, el: 0.95, dist: 16, fy: 2.2, panX: 0, panY: 0, twist: 0.012, veil: 0.42, signal: 0.1, highlight: 0 },
  // Full elevation next to the steps; floors light up as the steps progress.
  process: { az: 3.0, el: 0.08, dist: 19.5, fy: 4.1, panX: 0.18, panY: 0, twist: 0.012, veil: 0.04, signal: 0.25, highlight: 1 },
  // Close portrait on the left third, quote on the right.
  about: { az: 3.85, el: -0.02, dist: 14, fy: 4.4, panX: -0.29, panY: 0, twist: 0.012, veil: 0.06, signal: 0.4, highlight: 0 },
  // The crown, broadcasting.
  contact: { az: 4.7, el: 0.3, dist: 13, fy: 6.0, panX: 0.27, panY: -0.14, twist: 0.012, veil: 0.12, signal: 1, highlight: 0 },
}

// Portrait phones: the tower stays centred behind the text, so it is veiled more.
// Portrait phones: a smaller tower that drifts side to side behind the text, veiled for legibility.
const PORTRAIT: Record<SectionId, Pick<Shot, 'veil' | 'panX' | 'panY'>> = {
  home: { veil: 0.32, panX: 0.2, panY: 0.18 },
  services: { veil: 0.58, panX: -0.22, panY: 0 },
  work: { veil: 0.62, panX: 0.2, panY: 0 },
  process: { veil: 0.18, panX: 0.29, panY: 0.02 },
  about: { veil: 0.56, panX: 0.22, panY: 0 },
  contact: { veil: 0.5, panX: -0.18, panY: 0.1 },
}

interface Mark {
  id: SectionId
  el: HTMLElement
  key: number
}

export interface ChoreoResult {
  shot: Shot
  /** 0..1 progress through the Process steps. */
  process: number
}

export class Choreographer {
  private marks: Mark[] = []
  private stepsTop = 0
  private stepsHeight = 1
  private readonly result: ChoreoResult = { shot: { ...SHOTS.home }, process: 0 }

  constructor(
    sections: HTMLElement[],
    private readonly steps: HTMLElement,
  ) {
    this.marks = sections.map((el) => ({ id: el.dataset.section as SectionId, el, key: 0 }))
    this.measure()
  }

  /** Cache section positions (call on resize / layout changes). */
  measure(): void {
    const vh = layoutViewport().h
    const scrollY = window.scrollY
    for (const mark of this.marks) {
      const r = mark.el.getBoundingClientRect()
      const top = r.top + scrollY
      // The shot is fully "settled" when the section's centre meets the viewport centre.
      mark.key = Math.max(0, top + Math.min(r.height, vh * 1.4) / 2 - vh / 2)
    }
    const s = this.steps.getBoundingClientRect()
    this.stepsTop = s.top + scrollY
    this.stepsHeight = Math.max(1, s.height)
  }

  compute(y: number, aspect: number): ChoreoResult {
    const marks = this.marks
    let i = 0
    while (i < marks.length - 1 && y >= marks[i + 1].key) i++
    const a = marks[i]
    const b = marks[Math.min(i + 1, marks.length - 1)]
    const span = b.key - a.key
    // Hold each shot around its section, travel in between.
    const t = span > 0 ? smoothstep(0.12, 0.88, (y - a.key) / span) : 0

    // 0 = portrait phone, 1 = landscape desktop.
    const layout = smoothstep(0.8, 1.3, aspect)
    const out = this.result.shot
    for (const key of KEYS) out[key] = lerp(SHOTS[a.id][key], SHOTS[b.id][key], t)
    out.panX = lerp(lerp(PORTRAIT[a.id].panX, PORTRAIT[b.id].panX, t), out.panX, layout)
    out.veil = lerp(lerp(PORTRAIT[a.id].veil, PORTRAIT[b.id].veil, t), out.veil, layout)
    out.panY = lerp(lerp(PORTRAIT[a.id].panY, PORTRAIT[b.id].panY, t), out.panY, layout)
    out.dist *= lerp(1.5, 1, layout)

    const vh = layoutViewport().h
    this.result.process = clamp((y + vh * 0.62 - this.stepsTop) / this.stepsHeight)
    return this.result
  }
}
